import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ComponentType } from 'react';

import type { PromoRendererProps } from '@ps-int/promozavr/app/PromoRenderer';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import type { RootState } from '../../../../init-store';
import { isDOMAvailable } from '../../helpers';
import { getUserSelector, selectUserUid } from '../user/selectors';
import { getPromozavrDatasyncClient } from './datasync';
import { buildPromozavrRendererProps, type PromozavrRuntimeRendererProps } from './renderer-props';
import { getPromozavrRenderer, getPromozavrRendererSource } from './renderer-source';
import {
    buildPromozavrDebugSnapshot,
    buildPromozavrGetNow,
    buildPromozavrRuntimeViewState,
    getPromozavrTuningUrl,
    shouldDispatchPromozavrInit,
    shouldResetPromozavrState,
    syncPromozavrDebugSnapshot,
} from './runtime-helpers';
import { selectPromozavrRuntimeContext } from './selectors';
import { initPromozavr, resetPromozavrState, setPromozavrRendererSource } from './slice';
import { type PromozavrQueueKind, type PromozavrQueueSelection } from './types';

type PromozavrRuntimeRendererItem = {
    key: string;
    queue: PromozavrQueueKind;
    props: PromozavrRuntimeRendererProps;
};

export type PromozavrRuntimeController = {
    PromoRenderer: ComponentType<PromoRendererProps>;
    promoRenderers: PromozavrRuntimeRendererItem[];
    renderKey: string;
};

type ActiveQueues = Record<PromozavrQueueKind, boolean>;

export function usePromozavrRuntimeController(): PromozavrRuntimeController {
    const dispatch = useDispatch();
    const { pathname, search } = useLocation();
    const runtimeContext = useSelector((state: RootState) => selectPromozavrRuntimeContext(state, pathname));
    const userUid = useSelector(selectUserUid);
    const user = useSelector(getUserSelector);
    const [activeQueues, setActiveQueues] = useState<ActiveQueues>({
        regular: true,
        persistent: true,
    });

    const {
        shouldUsePaas,
        shouldUsePdokPromoRenderer,
        isDebugToolsEnabled,
        isYandexNet,
        isMessenger,
        isMeetingRoomIntegration,
        isTouch,
        appLang,
        yandex360Host,
        serverTime,
        bodyThemeName,
        status,
        request,
        requestKey,
        queueSelections,
        rendererSource,
        lastError,
    } = runtimeContext;
    const regularSelection = queueSelections.regular;
    const persistentSelection = queueSelections.persistent;

    const viewState = useMemo(
        () =>
            buildPromozavrRuntimeViewState(
                {
                    pathname,
                    search,
                    isYandexNet,
                    shouldUsePaas,
                    shouldUsePdokPromoRenderer,
                    isMessenger,
                    isMeetingRoom: isMeetingRoomIntegration,
                    bodyThemeName,
                    requestKey,
                    queueSelections,
                },
                getPromozavrRendererSource
            ),
        [
            bodyThemeName,
            isMeetingRoomIntegration,
            isMessenger,
            isYandexNet,
            pathname,
            queueSelections,
            requestKey,
            search,
            shouldUsePaas,
            shouldUsePdokPromoRenderer,
        ]
    );
    const {
        hasDebugQuery,
        shouldInit,
        nextRequestKey,
        nextRendererSource,
        shouldRender,
        shouldRenderRegular,
        shouldRenderPersistent,
        isDark,
    } = viewState;

    const deactivateRegular = useCallback(() => {
        setActiveQueues((currentActiveQueues) => ({
            ...currentActiveQueues,
            regular: false,
        }));
    }, []);

    const deactivatePersistent = useCallback(() => {
        setActiveQueues((currentActiveQueues) => ({
            ...currentActiveQueues,
            persistent: false,
        }));
    }, []);

    useEffect(() => {
        if (shouldRenderRegular || shouldRenderPersistent) {
            setActiveQueues({
                regular: true,
                persistent: true,
            });
        }
    }, [nextRequestKey, shouldRenderRegular, shouldRenderPersistent]);

    useEffect(() => {
        if (
            shouldResetPromozavrState({
                shouldInit,
                status,
                request,
                requestKey,
                queueSelections,
                rendererSource,
                lastError,
                nextRendererSource,
            })
        ) {
            dispatch(resetPromozavrState());
            return;
        }

        if (rendererSource !== nextRendererSource) {
            dispatch(setPromozavrRendererSource(nextRendererSource));
        }
    }, [
        dispatch,
        lastError,
        nextRendererSource,
        queueSelections,
        rendererSource,
        request,
        requestKey,
        shouldInit,
        status,
    ]);

    useEffect(() => {
        if (!shouldDispatchPromozavrInit({ shouldInit, requestKey, nextRequestKey })) {
            return;
        }

        dispatch(
            initPromozavr({
                pathname,
                search,
                requestKey: nextRequestKey,
            })
        );
    }, [dispatch, nextRequestKey, pathname, requestKey, search, shouldInit]);

    useEffect(() => {
        if (!isDOMAvailable()) {
            return;
        }

        syncPromozavrDebugSnapshot(
            window,
            buildPromozavrDebugSnapshot({
                pathname,
                shouldInit,
                status,
                requestKey,
                request,
                queueSelections,
                rendererSource,
                lastError,
            }),
            hasDebugQuery || isDebugToolsEnabled
        );
    }, [
        hasDebugQuery,
        isDebugToolsEnabled,
        lastError,
        pathname,
        queueSelections,
        rendererSource,
        request,
        requestKey,
        shouldInit,
        status,
    ]);

    const PromoRenderer = useMemo(() => getPromozavrRenderer(nextRendererSource), [nextRendererSource]);
    const getNow = useMemo(() => buildPromozavrGetNow(serverTime), [serverTime]);
    const dsClient = useMemo(() => {
        if (!shouldRender) {
            return undefined;
        }

        return getPromozavrDatasyncClient(userUid);
    }, [shouldRender, userUid]);
    const urls = useMemo<PromoRendererProps['urls']>(
        () => ({
            tuning: getPromozavrTuningUrl(yandex360Host),
        }),
        [yandex360Host]
    );

    const getPromoRendererProps = useCallback(
        ({
            selection,
            queue,
            isActive,
            deactivate,
        }: {
            selection: PromozavrQueueSelection;
            queue: PromozavrQueueKind;
            isActive: boolean;
            deactivate: () => void;
        }): PromozavrRuntimeRendererProps | undefined => {
            return buildPromozavrRendererProps({
                selection,
                queue,
                isActive,
                deactivate,
                isDark,
                getNow,
                dsClient,
                appLang,
                isTouch,
                urls,
                hasSubscription: Boolean(user?.hasProSubscription),
            });
        },
        [appLang, dsClient, getNow, isDark, isTouch, urls, user?.hasProSubscription]
    );

    const regularPromoRendererProps = useMemo<PromozavrRuntimeRendererProps | undefined>(() => {
        if (!shouldRenderRegular) {
            return undefined;
        }

        return getPromoRendererProps({
            selection: regularSelection,
            queue: 'regular',
            isActive: activeQueues.regular,
            deactivate: deactivateRegular,
        });
    }, [activeQueues.regular, deactivateRegular, getPromoRendererProps, regularSelection, shouldRenderRegular]);

    const persistentPromoRendererProps = useMemo<PromozavrRuntimeRendererProps | undefined>(() => {
        if (!shouldRenderPersistent) {
            return undefined;
        }

        return getPromoRendererProps({
            selection: persistentSelection,
            queue: 'persistent',
            isActive: activeQueues.persistent,
            deactivate: deactivatePersistent,
        });
    }, [
        activeQueues.persistent,
        deactivatePersistent,
        getPromoRendererProps,
        persistentSelection,
        shouldRenderPersistent,
    ]);

    const promoRenderers = useMemo<PromozavrRuntimeRendererItem[]>(() => {
        const renderers: PromozavrRuntimeRendererItem[] = [];

        if (regularPromoRendererProps) {
            renderers.push({
                key: `${nextRequestKey}:regular`,
                queue: 'regular',
                props: regularPromoRendererProps,
            });
        }

        if (persistentPromoRendererProps) {
            renderers.push({
                key: `${nextRequestKey}:persistent`,
                queue: 'persistent',
                props: persistentPromoRendererProps,
            });
        }

        return renderers;
    }, [nextRequestKey, persistentPromoRendererProps, regularPromoRendererProps]);

    return {
        PromoRenderer,
        promoRenderers,
        renderKey: nextRequestKey,
    };
}
